export const PAYMENT_PROVIDER_KEYS = Object.freeze({
  STRIPE: 'stripe',
  PAYPAL: 'paypal',
  MULTIPLE: 'multiple'
})

export const PAYMENT_PROVIDER_LABELS = Object.freeze({
  [PAYMENT_PROVIDER_KEYS.STRIPE]: 'Stripe',
  [PAYMENT_PROVIDER_KEYS.PAYPAL]: 'PayPal'
})

export const PAYMENT_PROVIDER_ICON_CLASSES = Object.freeze({
  STRIPE: 'fa-brands fa-stripe',
  PAYPAL: 'fa-brands fa-paypal'
})

export const PAYMENT_PROVIDER_CONFIGS = Object.freeze([
  {
    key: PAYMENT_PROVIDER_KEYS.STRIPE,
    title: PAYMENT_PROVIDER_LABELS[PAYMENT_PROVIDER_KEYS.STRIPE],
    iconClass: PAYMENT_PROVIDER_ICON_CLASSES.STRIPE,
    mark: 'S',
    subtitle: 'Card payments',
    description: 'Accept card payments through your connected Stripe account.',
    accountLabel: 'Stripe account'
  },
  {
    key: PAYMENT_PROVIDER_KEYS.PAYPAL,
    title: PAYMENT_PROVIDER_LABELS[PAYMENT_PROVIDER_KEYS.PAYPAL],
    iconClass: PAYMENT_PROVIDER_ICON_CLASSES.PAYPAL,
    mark: 'P',
    subtitle: 'Business payments',
    description: 'Accept PayPal payments through your connected business account.',
    accountLabel: 'PayPal merchant'
  }
])

// The skin is the question's own field, like every other question type. `payment.mode` is the
// pre-skin shape - kept as a fallback so forms built before skins keep working. The builder
// rewrites old questions to the new shape on load, so this only matters until each form is re-saved.
// TODO: remove the payment.mode fallback after 2026-11 once no form still stores it.
export function getPaymentSkinId(question) {
  return question?.skin?.id || question?.payment?.mode || PAYMENT_MODE_KEYS.FIXED
}

export const PAYMENT_MODE_KEYS = Object.freeze({
  FIXED: 'fixed',
  PRODUCTS: 'products'
})

export const PAYMENT_AMOUNT_TYPES = Object.freeze([
  { label: 'Fixed amount', value: PAYMENT_MODE_KEYS.FIXED },
  { label: 'Sell products', value: PAYMENT_MODE_KEYS.PRODUCTS }
])

// Reuse the exact same wording the builder itself uses for this choice (Payment Type: "Fixed
// amount" / "Sell products") instead of a separate made-up label for one of the two modes.
export const PAYMENT_MODE_LABELS = Object.freeze({
  [PAYMENT_MODE_KEYS.FIXED]: PAYMENT_AMOUNT_TYPES[0].label,
  [PAYMENT_MODE_KEYS.PRODUCTS]: PAYMENT_AMOUNT_TYPES[1].label
})

export const PAYMENT_QUANTITY_MODE_KEYS = Object.freeze({
  CUSTOMER: 'customer',
  FIXED: 'fixed'
})

export const PAYMENT_QUANTITY_MODES = Object.freeze([
  { label: 'Customer selectable', value: PAYMENT_QUANTITY_MODE_KEYS.CUSTOMER },
  { label: 'Fixed quantity', value: PAYMENT_QUANTITY_MODE_KEYS.FIXED }
])

// Currencies a payment question can use for `provider`, out of the fetched {stripe, paypal}
// list (paymentConnections Vuex state) - PayPal accepts far fewer than Stripe, so this keeps the
// picker from offering a currency the provider would reject at checkout.
export function getCurrenciesForProvider(provider, currencies) {
  const list = provider === PAYMENT_PROVIDER_KEYS.STRIPE ? currencies?.stripe : currencies?.paypal
  return list || []
}

export const PAYMENT_FIELD_OPTIONS = Object.freeze({
  amountTypes: PAYMENT_AMOUNT_TYPES,
  quantityModes: PAYMENT_QUANTITY_MODES
})

export const PAYMENT_DEFAULTS = Object.freeze({
  NAME: 'Payment',
  PRODUCT_NAME: 'Product',
  BUTTON_TEXT: 'Pay now',
  CURRENCY: 'USD',
  FIXED_AMOUNT: 10,
  DEFAULT_QUANTITY: 1,
  MIN_QUANTITY: 1,
  MAX_QUANTITY: 10
})

export const PAYMENT_MINOR_UNIT_DIVISOR = 100

// Mirrors FormPayment::ZERO_DECIMAL_CURRENCIES (api/app/Models/FormPayment.php) — these currencies
// are stored in major units, not cents, so amount must not be divided by 100 for display.
export const PAYMENT_ZERO_DECIMAL_CURRENCIES = Object.freeze([
  'BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA',
  'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF'
])

export const PAYMENT_RESPONSE_KEYS = Object.freeze({
  PAYMENT_REF: 'payment_ref',
  PROVIDER: 'provider',
  CURRENCY: 'currency',
  STATUS: 'status',
  AMOUNT: 'amount'
})

export const PAYMENT_RESPONSE_STATUSES = Object.freeze({
  PAID: 'paid',
  UNPAID: 'unpaid',
  FAILED: 'failed'
})

export const PAYMENT_SUBMISSION_LABELS = Object.freeze({
  AMOUNT: 'Amount',
  PAYMENT_REFERENCE: 'Payment reference',
  UNKNOWN_STATUS: 'Unknown'
})

export const createDefaultPaymentProduct = (id = `product_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`) => ({
  id,
  name: PAYMENT_DEFAULTS.PRODUCT_NAME,
  description: '',
  imageUrl: '',
  unitAmount: PAYMENT_DEFAULTS.FIXED_AMOUNT,
  quantityMode: PAYMENT_QUANTITY_MODE_KEYS.CUSTOMER,
  defaultQuantity: PAYMENT_DEFAULTS.DEFAULT_QUANTITY,
  minQuantity: PAYMENT_DEFAULTS.MIN_QUANTITY,
  maxQuantity: PAYMENT_DEFAULTS.MAX_QUANTITY,
  enabled: true
})

export const PAYMENT_CONNECTION_STATUS = Object.freeze({
  CONNECTED: {
    label: 'Connected',
    color: 'success'
  },
  NEEDS_SETUP: {
    label: 'Needs setup',
    color: 'accent-color'
  },
  NOT_CONNECTED: {
    label: 'Not connected',
    color: 'accent-color'
  },
  COMING_SOON: {
    label: 'Coming soon',
    color: 'accent-color'
  }
})

export const PAYMENT_STATUS_COLORS = Object.freeze({
  [PAYMENT_RESPONSE_STATUSES.PAID]: PAYMENT_CONNECTION_STATUS.CONNECTED.color,
  [PAYMENT_RESPONSE_STATUSES.UNPAID]: PAYMENT_CONNECTION_STATUS.NOT_CONNECTED.color,
  [PAYMENT_RESPONSE_STATUSES.FAILED]: 'danger-color',
  DEFAULT: 'grey'
})

export const PAYMENT_CONNECTION_LABELS = Object.freeze({
  CONNECTED_ACCOUNT: 'Connected account',
  NO_ACCOUNT_CONNECTED: 'No account connected',
  CONNECTED_SUFFIX: 'Connected',
  CONNECTED_AS_SUFFIX: 'as',
  PAYER_CHOICE: 'Let the payer choose'
})

export const PAYMENT_CONNECTION_STATE = Object.freeze({
  ACTIVE: 'active',
  INACTIVE: 'inactive'
})
